import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import { MonthViewProps, IEvent } from '../core/type';
import { OverlayTrigger } from 'react-bootstrap';
import { getEventCategoryColor, renderTooltip } from '../utils';
import { EventBar } from './eventbar';
import { RootState } from '../../../../../redux/store';
import { useSelector } from 'react-redux';
import { useAuth } from '../../../auth';
import { getEventDates, normalizeDate } from '../core/_utility';
import { getSaTodayDate } from '../../../../../utils/date';

// Constants
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const DAYS_IN_MONTH_VIEW = 42;
const ROW_HEIGHT_MULTIPLIER = 3;
const DEFAULT_CELL_HEIGHT = 80;
const HEADER_HEIGHT = 50;

// Helper to check if dates are the same day
const isSameDay = (date1: Date, date2: Date): boolean => {
    return date1.toDateString() === date2.toDateString();
};

const MonthView: React.FC<MonthViewProps> = React.memo(({
    currentDate,
    events,
    onDateClick,
    onMoreClick,
    onEventClick,
    addEventOnDateClick,
}) => {
    const { auth } = useAuth()
    const titleDisplayedRef = useRef<Set<string>>(new Set());
    const { categoryAndColorsData } = useSelector((state: RootState) => state.eventReducer);

    // Clear ref when events or month changes
    useEffect(() => {
        titleDisplayedRef.current.clear();
    }, [events, currentDate.getMonth(), currentDate.getFullYear()]);

    // Memoized current month/year for better performance
    const currentMonth = useMemo(() => currentDate.getMonth(), [currentDate]);
    const currentYear = useMemo(() => currentDate.getFullYear(), [currentDate]);
    const today = useMemo(() => getSaTodayDate(), []);

    const isToday = useCallback((date: Date): boolean => {
        return isSameDay(date, today);
    }, [today]);

    const isMultiDayEvent = useCallback((event: IEvent): boolean => {
        const { start, end } = getEventDates(event);
        if (!start || !end) return false;

        const startDate = normalizeDate(start);
        const endDate = normalizeDate(end);
        return startDate.getTime() !== endDate.getTime();
    }, []);

    // Optimized: Get events for a specific date without mutations
    const getEventsForDate = useCallback((date: Date): IEvent[] => {
        const targetDate = normalizeDate(date);

        return events.filter(event => {
            const { start, end } = getEventDates(event);

            if (!start || !end) return false;

            const startDate = normalizeDate(start);
            const endDate = normalizeDate(end);
            return targetDate >= startDate && targetDate <= endDate;
        });
    }, [events]);

    // Memoized calendar days grid
    const calendarDays = useMemo(() => {
        const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
        const startWeekday = firstDayOfMonth.getDay();
        const startDayOfGrid = new Date(firstDayOfMonth);
        startDayOfGrid.setDate(firstDayOfMonth.getDate() - startWeekday);

        return Array.from({ length: DAYS_IN_MONTH_VIEW }, (_, i) => {
            const day = new Date(startDayOfGrid);
            day.setDate(startDayOfGrid.getDate() + i);
            return day;
        });
    }, [currentYear, currentMonth]);

    // Optimized: Get week index using cached calendar days
    const getWeekIndex = useCallback((date: Date): number => {
        const start = calendarDays[0];
        if (!start) return 0;
        const diff = Math.floor((date.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return Math.floor(diff / 7);
    }, [calendarDays]);

    // Optimized: Calculate row height based on window size
    const rowHeight = useMemo(() => {
        if (typeof window === 'undefined') return DEFAULT_CELL_HEIGHT / ROW_HEIGHT_MULTIPLIER;
        const cellHeight = (window.innerHeight - HEADER_HEIGHT) / 12;
        return cellHeight / ROW_HEIGHT_MULTIPLIER;
    }, []);

    // Optimized: Check if event overlaps with another
    const doEventsOverlap = useCallback((event1: IEvent, event2: IEvent): boolean => {
        const { start: start1, end: end1 } = getEventDates(event1);
        const { start: start2, end: end2 } = getEventDates(event2);
        [start1, end1, start2, end2].forEach(date => {
            if (!date) return;
            normalizeDate(date);
        })
        return start1 <= end2 && start2 <= end1;
    }, []);

    // Optimized: Assign lanes to events for each week
    const eventsWithLanes = useMemo(() => {
        const weeksMap = new Map<number, Map<Number, IEvent>>();

        // Group events by week
        calendarDays.forEach(day => {
            const weekIndex = getWeekIndex(day);
            if (!weeksMap.has(weekIndex)) {
                weeksMap.set(weekIndex, new Map());
            }

            const weekEvents = weeksMap.get(weekIndex)!;
            const dayEvents = getEventsForDate(day);

            dayEvents.forEach(event => {
                if (!weekEvents.has(event.id)) {
                    weekEvents.set(event.id, event);
                }
            });
        });

        // Assign lanes
        const weeksWithLanes = new Map<number, Array<IEvent & { lane: number }>>();

        weeksMap.forEach((weekEventsMap, weekIndex) => {
            const weekEvents = Array.from(weekEventsMap.values());
            const lanes: IEvent[][] = [];

            const eventsWithLane = weekEvents.map(event => {
                let laneIndex = lanes.findIndex(lane =>
                    !lane.some(e => doEventsOverlap(e, event))
                );

                if (laneIndex === -1) {
                    laneIndex = lanes.length;
                    lanes.push([]);
                }

                lanes[laneIndex].push(event);
                return { ...event, lane: laneIndex };
            });

            weeksWithLanes.set(weekIndex, eventsWithLane);
        });

        return weeksWithLanes;
    }, [calendarDays, getWeekIndex, getEventsForDate, doEventsOverlap]);

    // Memoized total events in current month
    const totalMonthEvents = useMemo(() => {
        const monthStart = new Date(currentYear, currentMonth, 1);
        const monthEnd = new Date(currentYear, currentMonth + 1, 0);
        const normalizedStart = normalizeDate(monthStart);
        const normalizedEnd = normalizeDate(monthEnd);
        normalizedEnd.setHours(23, 59, 59, 999);

        return events.filter(event => {
            const { start, end } = getEventDates(event);
            if (!start || !end) return false;
            const eventStart = normalizeDate(start);
            const eventEnd = normalizeDate(end);
            return eventStart <= normalizedEnd && eventEnd >= normalizedStart;
        }).length;
    }, [events, currentYear, currentMonth]);

    // Calculate max visible events based on total count
    const getMaxVisible = useCallback(() => totalMonthEvents > 3 ? 2 : 3, [totalMonthEvents]);

    // DayCell Component - Extracted and memoized
    const DayCell = React.memo(({ day }: { day: Date }) => {
        const weekIndex = getWeekIndex(day);
        const weekEvents = eventsWithLanes.get(weekIndex) || [];
        const isCurrentMonth = day.getMonth() === currentMonth;
        const isTodayFlag = isToday(day);
        const maxVisible = getMaxVisible();

        // Filter events for this specific day
        const dayEvents = useMemo(() => {
            const targetDate = normalizeDate(day);
            return weekEvents.filter(event => {
                const { start, end } = getEventDates(event);

                const startDate = normalizeDate(start);
                const endDate = normalizeDate(end);
                return targetDate >= startDate && targetDate <= endDate;
            });
        }, [day, weekEvents]);

        // Assign unique lanes to ALL events (simple sequential assignment)
        const eventsWithUnifiedLanes = useMemo(() => {
            if (dayEvents.length === 0) {
                return [];
            }

            // Sort events to ensure consistent order
            const sortedEvents = [...dayEvents].sort((a, b) => {
                // Multi-day events first
                const aIsMulti = isMultiDayEvent(a);
                const bIsMulti = isMultiDayEvent(b);

                if (aIsMulti !== bIsMulti) {
                    return aIsMulti ? -1 : 1;
                }

                // Then by ID for consistency
                return a.id - b.id;
            });

            // Simply assign each event to a unique lane (index based)
            return sortedEvents.map((event, index) => ({
                ...event,
                unifiedLane: index
            }));
        }, [dayEvents, isMultiDayEvent]);

        // Calculate visible events based on maxVisible
        const visibleEvents = useMemo(() => {
            const visibleItems = eventsWithUnifiedLanes.slice(0, maxVisible);
            const hasMore = eventsWithUnifiedLanes.length > maxVisible && isCurrentMonth;

            return {
                visibleEvents: visibleItems,
                totalCount: eventsWithUnifiedLanes.length,
                visibleCount: visibleItems.length,
                hasMore
            };
        }, [eventsWithUnifiedLanes, maxVisible, isCurrentMonth]);

        // Event handlers
        const handleDateClick = useCallback((e: React.MouseEvent) => {
            e.stopPropagation();
            onDateClick(day);
        }, [day, onDateClick]);

        const handleMoreClick = useCallback((e: React.MouseEvent) => {
            e.stopPropagation();
            onMoreClick(day);
        }, [day, onMoreClick]);

        const handleEventClick = useCallback((e: React.MouseEvent, event: IEvent) => {
            e.stopPropagation();
            onEventClick(day, event);
        }, [day, onEventClick]);

        const handleAddEvent = useCallback(() => {
            addEventOnDateClick(day);
        }, [day, addEventOnDateClick]);

        const handleCellClick = useCallback(() => {
            if (auth) {
                handleAddEvent();
            }
        }, [auth, handleAddEvent]);

        return (
            <div
                className={`month-cell rounded-3 border-1 border-gray-200 border p-2 cursor-pointer ${isCurrentMonth ? 'bg-white' : 'bg-gray-100 bg-opacity-50 text-gray-500'
                    }`}
            >
                <div className="d-flex justify-content-between">
                    <div
                        onClick={handleDateClick}
                        className={`date-number fs-9 fw-semibold d-flex align-items-center justify-content-center rounded-circle ${isTodayFlag ? 'today-circle text-white shadow-sm' : ''
                            }`}
                    >
                        {day.getDate()}
                    </div>
                </div>

                <div
                    className="position-relative mt-1"
                    style={{
                        marginLeft: '-8px',
                        marginRight: '-8px',
                        height: `${rowHeight * ROW_HEIGHT_MULTIPLIER}px`,
                        overflow: 'hidden'
                    }}
                    onClick={handleCellClick}
                >
                    {/* Render all events with unique lanes - NO OVERLAP */}
                    {visibleEvents.visibleEvents.map((ev) => {
                        const isMulti = isMultiDayEvent(ev);
                        if (isMulti) {
                            const { start, end } = getEventDates(ev);

                            const eventStart = normalizeDate(start);
                            const eventEnd = normalizeDate(end);
                            const currentDateObj = normalizeDate(day);

                            return (
                                <EventBar
                                    key={`${ev.id}-${day.toISOString()}`}
                                    event={ev}
                                    day={day}
                                    lane={ev.unifiedLane}
                                    rowHeight={rowHeight}
                                    isFirstDay={currentDateObj.getTime() === eventStart.getTime()}
                                    isLastDay={currentDateObj.getTime() === eventEnd.getTime()}
                                    titleDisplayedRef={titleDisplayedRef.current}
                                    onEventClick={handleEventClick}
                                    viewType="month"
                                    currentDate={currentDate}
                                    showArrowIndicators={true}
                                />
                            );
                        } else {
                            const eventColor = getEventCategoryColor(ev.category, categoryAndColorsData);
                            const style: React.CSSProperties = {
                                position: 'absolute',
                                top: `${(ev.unifiedLane * rowHeight) + 2}px`,
                                left: '8px',
                                right: '8px',
                                backgroundColor: `${eventColor}20`,
                                borderLeft: `3px solid ${eventColor}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                padding: '1px 6px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            };

                            return (
                                <OverlayTrigger key={ev.id} overlay={renderTooltip(ev, categoryAndColorsData)}>
                                    <div
                                        className="fs-9 cursor-pointer"
                                        style={style}
                                        onClick={(e) => handleEventClick(e, ev)}
                                    >
                                        {ev.title}
                                    </div>
                                </OverlayTrigger>
                            );
                        }
                    })}

                    {/* "More" button */}
                    {visibleEvents.hasMore && (
                        <div
                            className="d-flex justify-content-center align-items-end"
                            style={{ height: `${rowHeight * ROW_HEIGHT_MULTIPLIER}px` }}
                        >
                            <button
                                className="more-btn fs-9 text-primary bg-opacity-0 border-0 fw-semibold p-0"
                                onClick={handleMoreClick}
                            >
                                +{visibleEvents.totalCount - visibleEvents.visibleCount} more
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    });

    return (
        <div className="bg-white overflow-x-auto">
            <div className="month-grid bg-white rounded-4 overflow-x-visible">
                {WEEK_DAYS.map(day => (
                    <div key={day} className="bg-gray-200 p-3 text-center font-bold text-gray-700 fs-7">
                        {day}
                    </div>
                ))}
                {calendarDays.map((day, idx) => (
                    <DayCell key={`${day.toISOString()}-${idx}`} day={day} />
                ))}
            </div>
        </div>
    );
});

export default MonthView;